// =============================================================================
// ui/state/useFacetUniverse.ts — the option list behind the Filters drawer.
// -----------------------------------------------------------------------------
// ⚠ ALL TIME, NOT THE ACTIVE RANGE. The old client counts facet options over the
// account-scoped set across the whole book, "so options stay stable as you
// narrow (they don't vanish when the view is empty)". Counting from the visible
// range makes the option list shuffle every time the date filter moves, and an
// option that vanished cannot be un-selected.
//
// Read ONCE per session and held: five columns over the whole book, which is a
// single page for any realistic user. Re-reading it whenever the drawer opened
// would be a request per click for data that changes only when a trade is added.
//
// ⚠ Account scoping is applied by the CALLER, not here — the drawer's account
// section has to keep offering every account, or unticking one would remove it
// from its own list.
// =============================================================================

import { useEffect, useState } from 'react';
import { loadFacetUniverse, type UniverseRow } from '../../io/sync/trades';
import { useSupabase } from './supabase';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface FacetUniverse {
  rows: readonly UniverseRow[] | null;
  error: string | null;
}

export function useFacetUniverse(): FacetUniverse {
  const { sb } = useSupabase();
  const [rows, setRows] = useState<readonly UniverseRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const r = await loadFacetUniverse(sb);
        if (!cancelled) setRows(r);
      } catch (e) {
        if (cancelled) return;
        // ⚠ Stays NULL, and the drawer renders its sections without counts. A
        // filter panel is not worth failing the app over — but an EMPTY option
        // list would read as "you have never traded these", so it must not be
        // presented as an answer either.
        setError(reportFailure('filter options', e));
      }
    })();

    return () => { cancelled = true; };
  }, [sb]);

  return { rows, error };
}
